import type {
	OsobyFyzickaOsobaDto,
	OsobyPodnikatelDto,
	RezaOpravneniDto,
} from '@gov-nx/api';
import type {
	KeyValueEntry,
	RezaPersonIdentity,
	RezaPersonIdentityFO,
	RezaPersonRecapLabels,
	RezaPersonRecapRow,
} from './rezaSwitcher.types';
import { zastupujiciOsobaNazev } from '@gov-nx/module/page';
import { nameFormatting, toDateReadable } from '@gov-nx/utils/common';
import { type Optional } from '@gov-nx/core/types';
import type { UseRezaSwitcherLocaleType } from './useRezaSwitcherLocale';

const empty = '-';

export const getRezaPersonIdentity = (
	activeReza: RezaOpravneniDto | null,
	person: Optional<OsobyFyzickaOsobaDto>,
	podnikateleSeznam: OsobyPodnikatelDto[] = []
): RezaPersonIdentity => {
	if (activeReza) {
		if (activeReza.zastupujiciOsobaTyp === 'FO') {
			const fyzickaOsoba = activeReza.zastupujiciOsoba?.fyzickaOsoba;

			return {
				typ: 'FO',
				prijmeni: fyzickaOsoba?.prijmeni,
				jmeno: fyzickaOsoba?.jmeno,
				datumNarozeni: fyzickaOsoba?.datumNarozeni,
				adresaText: fyzickaOsoba?.adresa?.adresaText,
			};
		}

		const pravnickaOsoba = activeReza.zastupujiciOsoba?.pravnickaOsoba;
		const ico = pravnickaOsoba?.ico;

		return {
			typ: 'PO',
			nazev: zastupujiciOsobaNazev(activeReza, { showIco: false }),
			ico,
			adresaText:
				pravnickaOsoba?.adresa?.adresaText ??
				podnikateleSeznam.find((p) => p.ico === ico)?.sidloSpolecnosti
					?.adresaText,
		};
	}

	return {
		typ: 'FO',
		prijmeni: person?.prijmeni,
		jmeno: person?.jmeno,
		datumNarozeni: person?.datumNarozeni,
		adresaText: person?.adresaPobytu?.adresaText,
	};
};

export const resolveRezaPersonRecapRows = (
	identity: RezaPersonIdentity,
	labels: RezaPersonRecapLabels
): RezaPersonRecapRow[] => {
	if (identity.typ === 'PO') {
		return [
			{ label: labels.nazev, value: identity.nazev },
			{
				label: labels.ico,
				value: identity.ico ? String(identity.ico) : undefined,
			},
			{ label: labels.adresaSidla, value: identity.adresaText },
		];
	}

	return [
		{ label: labels.prijmeni, value: nameFormatting(identity.prijmeni) },
		{ label: labels.jmeno, value: nameFormatting(identity.jmeno) },
		{
			label: labels.datumNarozeni,
			value: identity.datumNarozeni
				? toDateReadable(identity.datumNarozeni)
				: undefined,
		},
		{ label: labels.adresaTrvalehoBydliste, value: identity.adresaText },
	];
};

const getFOKeyValues = (
	identity: RezaPersonIdentityFO,
	t: UseRezaSwitcherLocaleType['t']
): Array<KeyValueEntry> => [
	{
		key: t('row.label.prijmeni'),
		value: identity.prijmeni ? nameFormatting(identity.prijmeni) : empty,
	},
	{
		key: t('row.label.jmeno'),
		value: identity.jmeno ? nameFormatting(identity.jmeno) : empty,
	},
	{
		key: t('row.label.datum-narozeni'),
		value: identity.datumNarozeni
			? toDateReadable(identity.datumNarozeni)
			: empty,
	},
	{
		key: t('row.label.adresa-pobytu'),
		value: identity.adresaText ?? empty,
	},
];

export const getTableKeyValues = (
	activeReza: RezaOpravneniDto | null,
	person: Optional<OsobyFyzickaOsobaDto>,
	podnikateleSeznam: OsobyPodnikatelDto[],
	t: UseRezaSwitcherLocaleType['t']
): Array<KeyValueEntry> => {
	const identity = getRezaPersonIdentity(activeReza, person, podnikateleSeznam);

	if (identity.typ === 'PO') {
		return [
			{
				key: t('row.label.nazev'),
				value: identity.nazev ?? empty,
			},
			{
				key: t('row.label.ico'),
				value: identity.ico ?? empty,
			},
			{
				key: t('row.label.adresa-sidla'),
				value: identity.adresaText ?? empty,
			},
		];
	}

	return getFOKeyValues(identity, t);
};

export const getSingleRezaOptionAsText = (
	options: Array<{ label: string; value: string }>
) => {
	if (options.length === 1) {
		return {
			singleOptionLabel: options[0].label,
			singleOptionValue: options[0].value,
			hasSingleOption: true,
		};
	}
	return undefined;
};
